import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Loader2, Check, Edit3 } from "lucide-react";
import { toast } from "sonner";
import { sendChat, submitChatQuote } from "../lib/api";

const STORAGE_KEY = "gr_chat_session_id";

// Field labels for the quote summary card
const QUOTE_FIELDS = [
  { key: "service", label: "Service" },
  { key: "property_type", label: "Property type" },
  { key: "units", label: "Units / size" },
  { key: "location", label: "Location" },
  { key: "urgency", label: "Urgency" },
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "message", label: "Issue" },
];

const INITIAL_MSG = {
  role: "assistant",
  content:
    "Hi, I'm Greenroute Assistant. I can help you describe a maintenance issue, give you a ballpark estimate, or build a quote request for our team. For emergencies, call +27 66 183 3893.",
};

export const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([INITIAL_MSG]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [submittingQuote, setSubmittingQuote] = useState(false);
  const [seed, setSeed] = useState(null); // {label, context} when launched from a blog post
  const scrollRef = useRef(null);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) setSessionId(stored);
  }, []);

  // Listen for "open with article context" events fired by blog posts.
  useEffect(() => {
    const onOpenWithSeed = (e) => {
      const { label, context, intro } = e.detail || {};
      // Start a fresh session so the seed scoping is clean
      localStorage.removeItem(STORAGE_KEY);
      setSessionId(null);
      setSeed({ label, context });
      setMessages([
        intro
          ? { role: "assistant", content: intro }
          : INITIAL_MSG,
      ]);
      setOpen(true);
    };
    window.addEventListener("gr:chat-seed", onOpenWithSeed);
    return () => window.removeEventListener("gr:chat-seed", onOpenWithSeed);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const submit = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;

    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);

    // If we have a seed context (from "Ask the article"), prefix it to the first
    // user message invisibly — and clear it so subsequent turns don't repeat.
    const wireText = seed?.context
      ? `[Article context — DO NOT QUOTE VERBATIM; use only as background for this conversation:\n${seed.context}\n]\n\nUser question:\n${text}`
      : text;
    if (seed) setSeed({ ...seed, context: null });

    try {
      const res = await sendChat(sessionId, wireText);
      if (res.session_id && !sessionId) {
        setSessionId(res.session_id);
        localStorage.setItem(STORAGE_KEY, res.session_id);
      }
      const assistantMsg = { role: "assistant", content: res.reply };
      if (res.quote) assistantMsg.quote = res.quote;
      setMessages((m) => [...m, assistantMsg]);
    } catch (err) {
      console.error(err);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Sorry, I'm having trouble responding right now. Please reach us directly at +27 66 183 3893 or admin@green-route.co.za.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onSubmitQuote = async (quote, msgIndex) => {
    if (!quote?.name || !quote?.email || !quote?.message) {
      toast.error("Missing required fields. Please ask the assistant to recap.");
      return;
    }
    setSubmittingQuote(true);
    try {
      await submitChatQuote({ ...quote, session_id: sessionId });
      toast.success("Quote submitted. Greenroute will be in touch within one business day.");
      setMessages((m) =>
        m.map((msg, i) =>
          i === msgIndex ? { ...msg, quote: { ...quote, _submitted: true } } : msg
        )
      );
    } catch (err) {
      console.error(err);
      toast.error("Couldn't submit. Please try again or call +27 66 183 3893.");
    } finally {
      setSubmittingQuote(false);
    }
  };

  const onEditQuote = () => {
    setInput("Actually, I'd like to change ");
    setTimeout(() => {
      const el = document.querySelector("[data-testid='chat-input']");
      if (el) el.focus();
    }, 50);
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="chatbot-toggle"
        aria-label="Open Greenroute Assistant"
        className="fixed bottom-24 right-6 z-[60] w-14 h-14 bg-forest text-cream flex items-center justify-center chat-bubble-shadow hover:bg-forest/90 transition-colors duration-300"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X size={22} />
            </motion.span>
          ) : (
            <motion.span key="m" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <MessageSquare size={22} />
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            key="chat-panel"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-44 right-6 z-[60] w-[92vw] max-w-md h-[600px] max-h-[78vh] bg-white border border-bordersoft chat-bubble-shadow flex flex-col"
            data-testid="chatbot-panel"
          >
            <div className="bg-forest text-cream px-5 py-4 flex items-center justify-between">
              <div>
                <div className="font-serif text-xl leading-tight">Greenroute Assistant</div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-cream/70">
                  {seed?.label ? `Discussing: ${seed.label}` : "AI · Quotes · 24/7"}
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-cream/80 hover:text-cream"
                aria-label="Close"
                data-testid="chatbot-close"
              >
                <X size={20} />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-4 bg-cream">
              {messages.map((m, i) => (
                <div key={i} data-testid={`chat-message-${i}`}>
                  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                        m.role === "user"
                          ? "bg-forest text-cream"
                          : "bg-white border border-bordersoft text-ink"
                      }`}
                    >
                      {m.content}
                    </div>
                  </div>
                  {m.quote && (
                    <QuoteCard
                      quote={m.quote}
                      submitted={m.quote._submitted}
                      submitting={submittingQuote}
                      onSubmit={() => onSubmitQuote(m.quote, i)}
                      onEdit={onEditQuote}
                    />
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex justify-start" data-testid="chat-loading">
                  <div className="bg-white border border-bordersoft text-ink px-4 py-3 text-sm">
                    <Loader2 size={16} className="animate-spin inline mr-2" />
                    Thinking…
                  </div>
                </div>
              )}
            </div>

            <form
              onSubmit={submit}
              className="border-t border-bordersoft bg-white p-3 flex items-center gap-2"
              data-testid="chat-form"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask, describe an issue, request a quote…"
                data-testid="chat-input"
                className="flex-1 px-3 py-3 bg-cream border border-bordersoft text-sm focus:outline-none focus:border-forest"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                data-testid="chat-send-btn"
                className="bg-forest text-cream p-3 hover:bg-forest/90 transition disabled:opacity-50"
                aria-label="Send"
              >
                <Send size={18} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const QuoteCard = ({ quote, submitted, submitting, onSubmit, onEdit }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    className="mt-3 bg-forest text-cream p-5 border border-gold/40"
    data-testid="quote-summary-card"
  >
    <div className="flex items-center gap-2 mb-4">
      <div className="uppercase text-[10px] tracking-[0.25em] text-gold">
        Quote request summary
      </div>
    </div>
    <dl className="space-y-2 text-sm">
      {QUOTE_FIELDS.map((f) =>
        quote[f.key] ? (
          <div key={f.key} className="flex gap-3" data-testid={`quote-row-${f.key}`}>
            <dt className="uppercase text-[10px] tracking-[0.2em] text-cream/60 w-24 shrink-0 mt-1">
              {f.label}
            </dt>
            <dd className="text-cream flex-1 break-words">{quote[f.key]}</dd>
          </div>
        ) : null
      )}
    </dl>

    {submitted ? (
      <div
        className="mt-5 flex items-center gap-2 text-gold text-sm font-medium"
        data-testid="quote-submitted-confirmation"
      >
        <Check size={16} /> Submitted — a specialist will contact you within one business day.
      </div>
    ) : (
      <div className="mt-5 flex gap-2">
        <button
          onClick={onSubmit}
          disabled={submitting}
          data-testid="quote-submit-btn"
          className="flex-1 bg-cream text-forest px-4 py-3 text-xs uppercase tracking-[0.2em] hover:bg-gold hover:text-cream transition-colors disabled:opacity-60"
        >
          {submitting ? "Submitting…" : "Submit to Greenroute"}
        </button>
        <button
          onClick={onEdit}
          data-testid="quote-edit-btn"
          className="px-4 py-3 border border-cream/40 text-cream text-xs uppercase tracking-[0.2em] hover:bg-cream/10 transition-colors"
          aria-label="Edit"
        >
          <Edit3 size={14} />
        </button>
      </div>
    )}
  </motion.div>
);

export default Chatbot;
